import React from 'react';
import { FormattedMessage } from 'react-intl';
import { StyleSheet, View, type ViewProps } from 'react-native';

import { BodyText, DisplayText, theme, useResponsive } from '@sg/garnish';

import { messages } from '../../CustomizationIngredientsGrid.messages';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Section label for the ingredient grid.
 *
 * NOTE: Unlike `Section`, this renders no scrollspy target and measures nothing, so
 *       it can be used as a row of a virtualized list where offscreen rows unmount.
 *       It carries no horizontal inset either, leaving that to the list it sits in.
 */
export const CustomizationIngredientsGridSectionHeading = (props: Props) => {
  const { ingredientKindName, subheading, helperText, style, ...restProps } = props;

  const { match } = useResponsive();

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = [match([styles.containerXS, styles.containerSM]), style];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles} {...restProps}>
      <View style={styles.label}>
        <DisplayText sizeMatch={['24']} bold style={styles.ingredientKindName}>
          <FormattedMessage
            {...messages.ingredientKind}
            values={{ ingredient_kind: ingredientKindName }}
          />
        </DisplayText>

        {subheading ? (
          <BodyText
            style={[match([styles.subheadingXS, styles.subheadingSM]), styles.subheading]}
            sizeMatch={['16', '18']}
            bold={match([false, true])}
          >
            {subheading}
          </BodyText>
        ) : null}
      </View>

      {helperText ? <BodyText style={styles.helperLabel}>{helperText}</BodyText> : null}
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    paddingTop: theme.spacing['8'],
  },
  containerSM: {
    paddingTop: theme.spacing['10'],
  },
  label: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  ingredientKindName: {
    flexShrink: 1,
  },
  subheading: {
    flexShrink: 0,
  },
  subheadingXS: {
    color: theme.colors.CHARCOAL,
  },
  subheadingSM: {
    color: theme.colors.BLACK,
  },
  helperLabel: {
    color: theme.colors.OPACITY.DARK_KALE.DARKER,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = ViewProps & {
  ingredientKindName: string;
  subheading?: string;
  helperText?: string | null;
};
